import { cn } from 'lib/utils';

/**
 * One label / control / unit row of a property form.
 *
 * Uses `display: contents` so its three cells become direct grid items of the
 * enclosing card body (FeatureCard bodyLayout="fields"). That is deliberate: a
 * per-row flex layout cannot align controls across rows, because each row sizes
 * its own columns. Sharing the parent's grid tracks means every label, every
 * control and every unit line up down the card — which is the whole point of a
 * horizontal form.
 *
 * The unit cell is always rendered, even when empty, so the columns stay aligned
 * in cards where only some rows carry a unit.
 */
/**
 * min-h-9 on every cell gives each row a 36px floor.
 *
 * Without it the row height came out of whatever else shared that grid row, so the
 * SAME three Submode radios measured 38.5px per row in one submode and 22.33px in
 * another (AstroDim5, measured) — the card visibly re-spaced itself when a Fade
 * Timing row appeared or disappeared beside it. A floor makes a property row a fixed
 * size regardless of its neighbours. Rows still grow past it when they genuinely need
 * to, e.g. a label wrapping to two lines, which is content driving height rather than
 * layout accident.
 */
const FieldRow = ({ label, unit, disabled, className, children }) => (
    <div className={cn('contents', className)}>
        {/* The label mutes with the control. Without this, a disabled row read exactly
            like an enabled one — the only hint was that clicking did nothing. */}
        {/* self-start on the label and the unit: the card body grid centres its items, so
            once a validation message makes the control cell taller than 36px the label and
            unit would float to the middle of the whole row and stop lining up with the input
            they belong to. Top aligned, each still centres its own text in its own 36px box,
            so a row without a message looks exactly as before. */}
        <div className={cn('flex min-h-9 min-w-0 items-center self-start text-sm leading-snug',
            disabled && 'text-muted-foreground')}>
            {label}
        </div>
        {/* A column, not a row: getIssuesComponent() is passed in as a sibling of the
            control, and laid out horizontally it sat BESIDE the input and ran on into the
            unit cell. Stacked, it reads under the control the way the legacy rows do
            (components/PropertyPicker puts its input column in flex-direction: column for
            exactly this).

            The max-w cap is load bearing. This cell sits in an `auto` grid track, so the
            track is sized by its max-content - an uncapped sentence like "The value must be
            between 9.00 and 17.00." would widen the control column of the whole card and
            squeeze the labels. Capping the message, not the cell, leaves the control itself
            free to be as wide as it needs (the Region dropdown is the widest). */}
        <div className={cn('flex min-h-9 flex-col items-end justify-center gap-1',
            '[&_.error-text]:max-w-[12rem] [&_.error-text]:text-right',
            '[&_.warning-text]:max-w-[12rem] [&_.warning-text]:text-right')}>
            {children}
        </div>
        <div className="flex min-h-9 items-center self-start whitespace-nowrap text-sm text-muted-foreground">
            {unit}
        </div>
    </div>
);

export default FieldRow;
